import React, { useEffect, useState } from "react";
import "./NewsApp.css";
import { ApiPaths } from "./../../Config/ApiPath";
import useAxiosHelper from "../../Common/AxiosHelper";
import { BasicInfo } from "../../Config/BasicInfo";
import moment from "moment";
import { Megaphone, X, Inbox, ChevronRight, CalendarDays, RefreshCw } from "lucide-react";

const PAGE_SIZE = 10; // get-all-news default page size

// Admin-written text may contain simple HTML; show it as plain text
const plain = (s) =>
  String(s || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .trim();
// one-line version for card previews (line-clamp + blank lines looks broken)
const preview = (s) => plain(s).replace(/\s+/g, " ");

const News = () => {
  const { AxiosGet } = useAxiosHelper();
  const [newsFetchData, setNewsFetchData] = useState(null); // null = loading
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    FetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // detail sheet: Escape closes, page behind stays put
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function FetchData(p = 1) {
    try {
      setError(false);
      if (p > 1) setLoadingMore(true);
      // status=1: only announcements the admin has published
      const q = new URLSearchParams({ news_section: 1, status: 1, page: p, limit: PAGE_SIZE });
      const tempData = await AxiosGet(`${ApiPaths.announcement}?${q.toString()}`);
      const list = Array.isArray(tempData?.data) ? tempData.data : [];
      setNewsFetchData((prev) => (p === 1 ? list : [...(prev || []), ...list]));
      setHasMore(list.length === PAGE_SIZE);
      setPage(p);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
      setError(true);
      setNewsFetchData((prev) => prev || []);
    } finally {
      setLoadingMore(false);
    }
  }

  const list = newsFetchData || [];
  const [featured, ...rest] = list;
  const when = (x) => (x?.date || x?.createdAt ? moment(x.date || x.createdAt) : null);
  const ago = (x) => when(x)?.fromNow() || "";
  const isNew = (x) => when(x)?.isAfter(moment().subtract(3, "days"));

  return (
    <section className="nwPage">
      <div className="nwHero">
        <span className="nwEyebrow">
          <Megaphone size={13} /> News
        </span>
        <h1>Announcements</h1>
        <p>Updates, launches and important notices from the Aethrx team.</p>
      </div>

      {newsFetchData === null ? (
        <div className="nwList">
          <div className="nwFeatured nwSkel">
            <span className="img" />
            <span className="a" />
            <span className="b" />
          </div>
          {[0, 1].map((i) => (
            <div key={i} className="nwItem nwSkel">
              <span className="thumb" />
              <span className="lines">
                <span className="a" />
                <span className="b" />
              </span>
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="nwEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>{error ? "Couldn't load announcements" : "No announcements yet"}</b>
          <small>{error ? "Check your connection and try again." : "New updates from the team will appear here."}</small>
          {error && (
            <button type="button" onClick={() => FetchData(1)}>
              <RefreshCw size={14} /> Retry
            </button>
          )}
        </div>
      ) : (
        <div className="nwList">
          {/* latest announcement, featured */}
          <article className="nwFeatured" onClick={() => setOpen(featured)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setOpen(featured)}>
            {featured?.image ? (
              <img src={featured.image} alt="" className="nwFeatImg" />
            ) : (
              <span className="nwFeatArt">
                <Megaphone size={40} />
              </span>
            )}
            <div className="nwFeatBody">
              <span className="nwMeta">
                {isNew(featured) && <em>New</em>}
                <CalendarDays size={12} /> {ago(featured)}
              </span>
              <h2>{featured?.title}</h2>
              <p>{preview(featured?.description)}</p>
              <span className="nwRead">
                Read more <ChevronRight size={15} />
              </span>
            </div>
          </article>

          {rest.map((x, i) => (
            <article
              key={x?._id || x?.id || i}
              className="nwItem"
              onClick={() => setOpen(x)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setOpen(x)}
            >
              {x?.image ? (
                <img src={x.image} alt="" className="nwThumb" />
              ) : (
                <span className="nwThumb art">
                  <Megaphone size={20} />
                </span>
              )}
              <span className="nwItemText">
                <span className="nwMeta">
                  {isNew(x) && <em>New</em>}
                  {ago(x)}
                </span>
                <b>{x?.title}</b>
                <small>{preview(x?.description)}</small>
              </span>
              <ChevronRight size={18} className="nwChev" />
            </article>
          ))}

          {hasMore && (
            <button type="button" className="nwMore" onClick={() => FetchData(page + 1)} disabled={loadingMore}>
              {loadingMore ? <span className="nwSpin" /> : null}
              {loadingMore ? "Loading…" : "Load older announcements"}
            </button>
          )}
        </div>
      )}

      {/* ---------- Detail sheet ---------- */}
      {open && (
        <div className="nwOverlay" onClick={() => setOpen(null)}>
          <div className="nwSheet" role="dialog" aria-modal="true" aria-labelledby="nwDetailTitle" onClick={(e) => e.stopPropagation()}>
            <span className="nwHandle" aria-hidden="true" />
            <button type="button" className="nwClose" onClick={() => setOpen(null)} aria-label="Close">
              <X size={18} />
            </button>
            {open.image && <img src={open.image} alt="" className="nwSheetImg" />}
            <span className="nwMeta">
              <CalendarDays size={12} />
              {when(open)?.format("DD MMM YYYY, hh:mm A") || ""}
            </span>
            <h3 id="nwDetailTitle">{open.title}</h3>
            <p className="nwSheetText">{plain(open.description)}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default News;
